/**
 * VirtualTileGrid — v1.3.72 performance shim for Browse.jsx.
 *
 * Renders an arbitrarily-large array of poster tiles using
 * `react-window`'s v2 `Grid` so only the rows currently scrolled
 * into view are mounted into the DOM. Replaces the CSS-grid full
 * render that was dropping frames at 14 k movies / 55 k episodes.
 *
 * Responsive: number of columns is derived from the container's
 * measured width using the same breakpoints the static CSS grid
 * used, so the visual density is identical to v1.3.71.
 *
 * v1.3.75 — Smart-TV D-pad tuning (inspired by ITVZA's
 * `ORPlayerSpeedyLinearLayoutManager`):
 *   • `overscanCount` is 10 (not 2) when `tvLayout=true` so the
 *     focused tile + 9 rows ahead are pre-rendered → no flash of
 *     unstyled cells when the remote D-pad slams down at full speed.
 *   • PageUp / PageDown / Home / End / channel-up / channel-down
 *     keys jump-scroll by a full viewport instead of one row.
 *   • Container reports `data-tv-grid` so global keydown handlers
 *     can target it without re-running CSS selectors.
 *
 * v1.4.51 — ROOT-CAUSE FIX for the "tile continuously enlarges when
 * focused" bug on Google TV / Android WebView in large (virtualized)
 * categories:
 *
 * The previous code defined `CellComponent` inside VirtualTileGrid
 * via `useCallback(fn, [items, columnCount, renderTile, getKey,
 * rowCount])`. Because Browse.jsx passes `renderTile={(item) => ...}`
 * as a NEW arrow function on every render, `renderTile` was a fresh
 * reference every render → `CellComponent` was a fresh reference
 * every render → react-window's internal `useMemo(() => memo(cell), [cell])`
 * created a NEW memoised cell TYPE → React UNMOUNTED and REMOUNTED
 * every visible cell on every parent render.
 *
 * When the focused cell's DOM node is destroyed and re-created many
 * times per second, the WebView loses focus, immediately re-focuses
 * the new element, re-runs `focus-within` CSS transitions, and each
 * mount briefly triggers `scrollIntoView`. The compound effect on
 * Android WebView is a visual "growing" of the focused poster because
 * the outline / border keeps rendering on top of a slightly-shifted
 * new element.
 *
 * Non-virtual categories (`< 200` items) never hit this because they
 * render as plain React children with `memo` — a `renderTile` change
 * has no effect there.
 *
 * Fix: keep `CellComponent` identity stable across renders (empty
 * `useCallback` deps) and flow live `items` / `renderTile` / `getKey`
 * / `columnCount` / `rowCount` through react-window's `cellProps`
 * channel so cells re-RENDER (React updates the same DOM) instead
 * of RE-MOUNTING (React destroys + recreates DOM). Focus is
 * preserved, no visual churn, no "growing" tiles.
 *
 *     <VirtualTileGrid
 *        items={filteredItems}
 *        renderTile={(item) => <ContentTile ... />}
 *        getKey={(item) => item.id}
 *        tvLayout={tvLayout}
 *     />
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Grid } from "react-window";

// Mirror Browse.jsx grid-cols-* breakpoints. Returns the column count
// for a given container width.
function columnsForWidth(width, tvLayout) {
  if (tvLayout) return 6; // TV mode is always 6 columns regardless.
  if (width >= 2200) return 8; // 3xl
  if (width >= 1536) return 7; // 2xl
  if (width >= 1280) return 6; // xl
  if (width >= 1024) return 5; // lg
  if (width >= 768)  return 4; // md
  if (width >= 640)  return 3; // sm
  return 2;
}

const GAP_PX = 16;            // matches `gap-4` (sm: gap-3 — we round up)
const TILE_ASPECT = 3 / 2;    // posters: width × (3/2) = height
const TITLE_BAND = 56;        // space below the poster for title + meta

export default function VirtualTileGrid({
  items,
  renderTile,
  getKey,
  tvLayout = false,
  className = "",
  // Approximate viewport-relative height — we let react-window auto-
  // size to its container via `defaultHeight` and the page scroll.
  minHeight = 800,
}) {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(
    typeof window !== "undefined" ? window.innerHeight : 900,
  );

  // Measure the container width with ResizeObserver so column count
  // updates on window resize / DevTools open / orientation change.
  //
  // v1.4.57 → v1.4.60 — Runaway "growing tile" bug on Android WebView
  // was traced to the container measuring ITS OWN width, then react-
  // window's inner scrollable Grid canvas expanding based on that
  // width, which then leaked back through `contentRect.width` on the
  // next observer callback. Positive feedback loop → the tile grew
  // from ~300 px to 5000+ px on the user's device.
  //
  // The v1.4.57 fix (clamp at `window.innerWidth`) turned out to be
  // insufficient because Android WebView reports the internal WebView
  // canvas width as `window.innerWidth` when the WebView is set to
  // use wide viewport mode (see `MainActivity.java` v1.4.53). So the
  // clamp value was itself inflated.
  //
  // v1.4.60 definitive fix:
  //   1. Read the PARENT element's `clientWidth` instead of our own.
  //      The parent is layout-driven by the Browse page's flex/grid
  //      chain and cannot be inflated by our own children.
  //   2. Also cap the resulting `colWidth` at 400 px absolute
  //      regardless of container width. On real hardware, a poster
  //      never needs to be wider than 400 px — this is the belt-and-
  //      braces safety net that guarantees no tile can visually blow
  //      up even if steps 1–3 all fail on some future Android build.
  //   3. Debounce updates to > 4 px changes so sub-pixel jitter from
  //      scrollbars / fractional pixel rounding can't restart the
  //      loop on every frame.
  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const readParentWidth = () => {
      const parent = el.parentElement;
      if (!parent) return el.clientWidth;
      // Use the parent's inner content width. Cannot be inflated by us.
      const pw = parent.clientWidth;
      // Extra viewport guard in case the parent itself is weird.
      const iw = typeof window !== "undefined" ? window.innerWidth : 1920;
      return Math.min(pw, iw);
    };
    const ro = new ResizeObserver(() => {
      const w = readParentWidth();
      if (w <= 0) return;
      setWidth((prev) => (Math.abs(prev - w) < 4 ? prev : w));
    });
    // Observe the PARENT, not ourselves — parent is layout-driven,
    // its width is a source of truth and cannot be inflated by us.
    if (el.parentElement) ro.observe(el.parentElement);
    else ro.observe(el);
    setWidth(readParentWidth());
    return () => ro.disconnect();
  }, []);

  // v1.3.72 nit-fix — viewport height needs its own listener
  // because ResizeObserver on the container only fires on horizontal
  // size changes (height is unconstrained inside the scroll parent).
  useEffect(() => {
    const onResize = () => setViewportHeight(window.innerHeight);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const columnCount = useMemo(
    () => columnsForWidth(width || 1280, tvLayout),
    [width, tvLayout],
  );
  const rowCount = Math.ceil((items?.length || 0) / Math.max(1, columnCount));
  // v1.4.60 — HARD CAP colWidth at 400 px. On any real display a poster
  // never needs to be wider than that; this stops the "growing tile"
  // bug even if the parent-width read somehow returns an inflated
  // value on a future Android build we haven't tested. It's the last
  // line of defence in the multi-layered fix (see the ResizeObserver
  // comment above for layers 1–3).
  const MAX_COL_WIDTH = 400;
  const colWidth = Math.min(
    MAX_COL_WIDTH,
    Math.max(80, Math.floor((width - GAP_PX * (columnCount - 1)) / columnCount)),
  );
  const rowHeight = Math.floor(colWidth * TILE_ASPECT) + TITLE_BAND + GAP_PX;

  // v1.4.51 — Data flows to cells via `cellProps` (react-window's
  // supported channel) instead of closing over the parent's state
  // inside `useCallback`. This makes `CellComponent` identity stable
  // across every render, so react-window's internal
  // `useMemo(() => memo(cell), [cell])` returns the SAME memoised
  // cell type each render → React does NOT unmount + remount cells
  // when Browse re-renders (search input, watched-tick, tab switch,
  // etc.). Focus is preserved and the WebView never sees a stream
  // of focus-in/focus-out events on the currently-selected tile.
  //
  // We still let cells re-render (via cellProps changing) so they
  // pick up fresh items — otherwise switching category would leave
  // the DOM showing the previous category's posters. `ContentTile` /
  // `SeriesTile` are wrapped in `React.memo` so unchanged tiles bail
  // out during the re-render pass and stay cheap.
  const cellProps = useMemo(
    () => ({ items, renderTile, getKey, columnCount, rowCount }),
    [items, renderTile, getKey, columnCount, rowCount],
  );

  // Memoise the cell renderer so react-window can reuse cells.
  const CellComponent = useCallback(
    ({
      columnIndex,
      rowIndex,
      style,
      items: cellItems,
      renderTile: cellRenderTile,
      getKey: cellGetKey,
      columnCount: cellCols,
      rowCount: cellRows,
    }) => {
      const idx = rowIndex * cellCols + columnIndex;
      if (idx >= cellItems.length) return null;
      const item = cellItems[idx];
      const key = cellGetKey ? cellGetKey(item) : idx;
      return (
        <div
          key={key}
          style={{
            ...style,
            // Add right/bottom gap inside the cell so the visual
            // spacing matches the old CSS grid `gap-4`. Skip the
            // gap on the last column / row to avoid trailing white.
            paddingRight: columnIndex === cellCols - 1 ? 0 : GAP_PX,
            paddingBottom: rowIndex === cellRows - 1 ? 0 : GAP_PX,
            // v1.0.2 — Own compositor layer per cell so recycled tiles
            // never bleed the previous cell's bitmap through on Android
            // WebView. `contain: layout paint` isolates the layer
            // without touching `transform` (which is locked to `none`
            // by the v1.4.53 focus-scale defence in vu-theme.css).
            backgroundColor: "#050f0c",
            contain: "layout paint",
          }}
        >
          {cellRenderTile(item, idx)}
        </div>
      );
    },
    [],
  );

  // While we haven't measured the container yet (first paint) fall
  // back to the static CSS grid so users with super-fast first
  // paints don't see a flash of empty space.
  if (width === 0) {
    return (
      <div
        ref={containerRef}
        className={`grid gap-3 sm:gap-4 ${
          tvLayout
            ? "grid-cols-6"
            : "grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 3xl:grid-cols-8"
        } ${className}`}
      >
        {/* Render first row eagerly so LCP isn't blank. */}
        {(items || []).slice(0, 14).map((it, i) => (
          <div key={getKey ? getKey(it) : i}>{renderTile(it, i)}</div>
        ))}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        width: "100%",
        // v1.4.57 — Hard clip to prevent runaway growth (see
        // ResizeObserver comment above). If for any reason the
        // internal react-window scroll canvas overflows, this
        // clip stops it from bleeding out to the viewport and
        // makes the bug visually noticeable instead of silently
        // growing.
        maxWidth: "100%",
        overflowX: "hidden",
        boxSizing: "border-box",
        // v1.0.2 — Solid opaque background + explicit `isolation` layer
        // so Android WebView's GPU compositor can't bleed previous
        // frames of recycled cells through as purple rectangles / ghost
        // outlines. `contain: layout paint` promotes this to its own
        // stacking context.
        backgroundColor: "#050f0c",
        isolation: "isolate",
        contain: "layout paint",
      }}
      data-tv-grid={tvLayout ? "true" : undefined}
      onKeyDown={(e) => {
        // v1.3.75 — D-pad jump-scroll on TV. PageUp / PageDown jump
        // by ~80% of the viewport, Home/End jump to start/end. We
        // delegate the actual scroll to the inner react-window Grid
        // via its scrollable wrapper (a <div> with overflow: auto).
        if (!tvLayout) return;
        const scroller = e.currentTarget.querySelector('[role="grid"], div[style*="overflow"]');
        if (!scroller) return;
        const px = Math.floor(scroller.clientHeight * 0.8);
        const lastTop = scroller.scrollHeight - scroller.clientHeight;
        let next = null;
        switch (e.key) {
          case "PageDown":
          case "MediaTrackNext":
            next = Math.min(lastTop, scroller.scrollTop + px); break;
          case "PageUp":
          case "MediaTrackPrevious":
            next = Math.max(0, scroller.scrollTop - px); break;
          case "Home":
            next = 0; break;
          case "End":
            next = lastTop; break;
          default: return;
        }
        e.preventDefault();
        scroller.scrollTo({ top: next, behavior: "auto" });
      }}
      tabIndex={tvLayout ? 0 : undefined}
    >
      <Grid
        cellComponent={CellComponent}
        // v1.4.51 — Data flows through here so cells re-render when
        // `items` / `renderTile` change (see cellProps `useMemo` above).
        // The `CellComponent` identity stays stable across parent
        // renders, so react-window keeps the SAME memoised cell type
        // and React does NOT unmount + remount cells. This is the
        // fix for the "focused tile grows continuously" bug on
        // Google TV / Android WebView in large (virtualized)
        // categories — the previous inline-renderer pattern caused
        // every cell to remount on every Browse re-render, which the
        // TV WebView interpreted as a stream of focus events on the
        // selected poster.
        cellProps={cellProps}
        columnCount={columnCount}
        columnWidth={colWidth + (columnCount > 1 ? GAP_PX : 0)}
        rowCount={rowCount}
        rowHeight={rowHeight}
        defaultWidth={width}
        defaultHeight={Math.max(minHeight, rowHeight * Math.min(rowCount, 6))}
        // v1.3.75 — overscan tuned for D-pad: TV mode pre-renders
        // 10 rows ahead so the focused tile + a long press of DOWN
        // never lands on an empty placeholder. Desktop stays at 2.
        overscanCount={tvLayout ? 10 : 2}
        style={{
          width: "100%",
          // Cap the grid height at viewport minus chrome (~200 px for
          // nav + sidebar). Keeps the layout sane on huge catalogs.
          height: Math.min(
            Math.max(minHeight, rowHeight * Math.min(rowCount, 6)),
            Math.max(400, viewportHeight - 200),
          ),
        }}
      />
    </div>
  );
}
